/**
 * Voice-to-text control for chat inputs (Web Speech API).
 * Shown on photondev (hostname) and/or when config enableVoiceInput is true.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Text } from "@fluentui/react-components";
import { Mic24Regular, MicOff24Regular } from "@fluentui/react-icons";
import { loadConfig } from "../config";

/** Test / dev portal host — show mic even if deployed config.json predates enableVoiceInput. */
function isPhotonDevHost(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname === "photondev.lenslock.com";
}

/** Minimal Web Speech API surface (Chromium `SpeechRecognition`). */
interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((ev: SpeechRecognitionResultEvent) => void) | null;
  onerror: ((ev: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionResultEvent {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: {
      isFinal: boolean;
      0: { transcript: string };
    };
  };
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

function speechErrorMessage(code: string): string {
  switch (code) {
    case "not-allowed":
      return "Microphone blocked. Allow mic for this site in the browser address bar.";
    case "audio-capture":
      return "No microphone found or it is in use by another app.";
    case "network":
      return "Speech service unreachable (network or policy). Try again or another network.";
    case "service-not-allowed":
      return "Speech recognition is disabled by browser or policy.";
    case "aborted":
      return "";
    case "no-speech":
      return "No speech heard. Click the mic and speak.";
    default:
      return code ? `Voice input error: ${code}` : "Voice input failed.";
  }
}

export interface VoiceTextInputButtonProps {
  value: string;
  onChange: (next: string) => void;
  /** When set, stopping dictation (click mic to turn off) calls this with the final text so the host can submit without pressing Send. */
  onDictationComplete?: (text: string) => void;
  disabled?: boolean;
}

export const VoiceTextInputButton: React.FC<VoiceTextInputButtonProps> = ({
  value,
  onChange,
  onDictationComplete,
  disabled,
}) => {
  const [visible, setVisible] = useState(() => isPhotonDevHost());
  const [listening, setListening] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const prefixRef = useRef("");
  const finalsRef = useRef("");
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  /** Mirrors the last full string passed to `onChange` during this dictation session. */
  const lastTranscriptRef = useRef("");
  const blockAutoSubmitRef = useRef(false);
  /** True only when the user clicked the mic to stop dictation (not on error/cleanup). */
  const userStoppedDictationRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    loadConfig().then((cfg) => {
      if (!cancelled) {
        setVisible(Boolean(cfg.enableVoiceInput) || isPhotonDevHost());
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const releaseMic = useCallback(() => {
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
  }, []);

  const stopRecognition = useCallback(() => {
    const rec = recognitionRef.current;
    if (rec) {
      try {
        rec.abort();
      } catch {
        try {
          rec.stop();
        } catch {
          /* ignore */
        }
      }
      recognitionRef.current = null;
    }
    releaseMic();
    setListening(false);
  }, [releaseMic]);

  const startRecognition = useCallback(async () => {
    setHint(null);
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setUnsupported(true);
      setHint("Voice input needs Chrome or Edge (Web Speech API).");
      return;
    }

    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = err instanceof DOMException ? err.name : "";
      const denied =
        name === "NotAllowedError" || name === "SecurityError" || name === "NotReadableError";
      setHint(
        denied
          ? "Microphone access was blocked. In Chrome or Edge, open site settings from the tune or lock icon left of the address bar, set Microphone to Allow, then try again."
          : `Microphone error: ${name || String(err)}`,
      );
      return;
    }
    mediaStreamRef.current = stream;

    prefixRef.current = value;
    finalsRef.current = "";
    lastTranscriptRef.current = value;
    blockAutoSubmitRef.current = false;
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    rec.maxAlternatives = 1;

    rec.onresult = (event: SpeechRecognitionResultEvent) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const piece = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalsRef.current += piece;
        else interim += piece;
      }
      const base = prefixRef.current;
      const spoken = finalsRef.current + interim;
      let nextValue: string;
      if (!base) {
        nextValue = spoken;
      } else {
        const joiner = spoken ? (base.endsWith(" ") ? "" : " ") : "";
        nextValue = base + joiner + spoken;
      }
      lastTranscriptRef.current = nextValue;
      onChange(nextValue);
    };

    rec.onerror = (event: SpeechRecognitionErrorEvent) => {
      const code = event.error || "";
      if (code === "aborted") return;
      blockAutoSubmitRef.current = true;
      const msg = speechErrorMessage(code);
      if (msg) setHint(msg);
      stopRecognition();
    };

    rec.onend = () => {
      recognitionRef.current = null;
      releaseMic();
      setListening(false);
      const text = lastTranscriptRef.current.trim();
      const userStopped = userStoppedDictationRef.current;
      userStoppedDictationRef.current = false;
      const blocked = blockAutoSubmitRef.current;
      blockAutoSubmitRef.current = false;
      if (
        onDictationComplete &&
        userStopped &&
        !blocked &&
        text
      ) {
        queueMicrotask(() => onDictationComplete(text));
      }
    };

    try {
      rec.start();
      recognitionRef.current = rec;
      setListening(true);
    } catch (e) {
      releaseMic();
      setHint(`Could not start voice input: ${String(e)}`);
    }
  }, [onChange, onDictationComplete, releaseMic, stopRecognition, value]);

  const toggle = useCallback(() => {
    if (listening) {
      userStoppedDictationRef.current = true;
      stopRecognition();
      setHint(null);
      return;
    }
    userStoppedDictationRef.current = false;
    void startRecognition();
  }, [listening, startRecognition, stopRecognition]);

  useEffect(() => {
    return () => {
      const rec = recognitionRef.current;
      if (rec) {
        try {
          rec.abort();
        } catch {
          /* ignore */
        }
      }
      recognitionRef.current = null;
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  if (!visible) return null;

  const aria = unsupported
    ? "Speech recognition not available"
    : listening
      ? "Stop dictation"
      : "Start dictation";

  return (
    <div
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        maxWidth: 200,
      }}
    >
      <Button
        appearance={listening ? "primary" : "secondary"}
        type="button"
        icon={listening ? <MicOff24Regular /> : <Mic24Regular />}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggle();
        }}
        disabled={disabled || unsupported}
        aria-pressed={listening}
        aria-label={aria}
        title={
          listening
            ? onDictationComplete
              ? "Stop — message will send"
              : "Stop dictation"
            : onDictationComplete
              ? "Dictate — click again when done to send"
              : "Dictate with microphone"
        }
      />
      {hint && (
        <Text size={100} style={{ color: "#a4262c", textAlign: "center", lineHeight: 1.2 }}>
          {hint}
        </Text>
      )}
    </div>
  );
};
